import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { MdLocationOn, MdPeople, MdBusiness } from 'react-icons/md';
import { FaUsers } from 'react-icons/fa';
import type { RootState, AppDispatch } from '../../redux/store';
import { fetchDemographicsRequest } from '../../redux/slices/demographicsSlice';
import CollapsibleCard from './CollapsibleCard';
import StatCard from './StatCard';
import PageHeader from '../common/PageHeader';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import EmptyState from '../common/EmptyState';
import type { TabType } from './types';

const Demographics: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { data, segments, industries, loading, error } = useSelector(
    (state: RootState) => state.demographics
  );
  const [activeTab, setActiveTab] = useState<TabType>('segments');
  const [expandedIndex, setExpandedIndex] = useState<number>(0);

  useEffect(() => {
    // Only fetch demographics data if it's not already in Redux
    if (restaurantId && !data) {
      dispatch(fetchDemographicsRequest(restaurantId));
    }
  }, [dispatch, restaurantId, data]);

  if (loading) {
    return <LoadingState page="demographics" />;
  }

  if (error) {
    return (
      <ErrorState 
        message="Failed to load demographics" 
        error={error}
        onRetry={() => restaurantId && dispatch(fetchDemographicsRequest(restaurantId))}
      />
    );
  }

  if (!data) {
    return <EmptyState message="No demographics data available" />;
  }

  return (
    <div className="min-h-screen bg-black p-">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <PageHeader 
          title="Demographics for"
          userName="Delaney"
          showBackButton={true}
        />

        {/* Location Info */}
        <div className="mb-2">
          <p className="text-brand-primary text-xs flex items-center gap-1">
            <MdLocationOn size={16} className='text-xs' />
            {data.state}
          </p>
        </div>

        {/* Stats and Summary Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          {/* Left: Stats Cards */}
          <div className="space-y-4">
            <StatCard
              value={data.population.toLocaleString()}
              label="Population"
              icon={<FaUsers size={40} />}
            />
            <StatCard
              value={`$ ${data.median_household_income.toLocaleString()}`}
              label="Median Income"
              icon={<MdBusiness size={40} />}
            />
            <StatCard
              value={data.median_age}
              label="Median Age"
              icon={<MdPeople size={40} />}
            />
          </div>

          {/* Right: Actionable Insights */}
          <div className="lg:col-span-3">
            <div 
              className="rounded-xl p-6 h-full"
              style={{
                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 14px 0px',
                background: '#212121',
                border: '1px solid rgba(224, 224, 224, 0.13)'
              }}
            >
              <h3 className="text-lg font-medium text-brand-primary mb-2">Actionable Insights</h3>
              <p className="text-gray-300 leading-relaxed font-light text-md">{data.demographic_summary}</p>
            </div>
          </div>
        </div>

        {/* Tab Section with Background */}
        <div 
          className="rounded-xl p-6"
          style={{
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 14px 0px',
            background: '#212121',
            border: '1px solid rgba(224, 224, 224, 0.13)'
          }}
        >
          {/* Tab Switcher */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => setActiveTab('segments')}
              className={`p-4 cursor-pointer rounded-xl text-md font-medium flex items-center justify-center gap-2 transition-all ${
                activeTab === 'segments'
                  ? 'bg-brand-primary text-[#ffffff]'
                  : 'bg-[#000000] text-[#ffffff] '
              }`}
              style={{
            border: '1px solid rgba(224, 224, 224, 0.13)'
          }}
            >
              <FaUsers size={20} />
              Key Demographic Segments
            </button>
            <button
              onClick={() => setActiveTab('industries')}
              className={`p-4 cursor-pointer rounded-xl text-md font-medium flex items-center justify-center gap-2 transition-all ${
                activeTab === 'industries'
                  ? 'bg-brand-primary text-[#ffffff]'
                  : 'bg-[#000000] text-gray-400 '
              }`}
               style={{
            border: '1px solid rgba(224, 224, 224, 0.13)'
          }}
            >
              <MdBusiness size={24} />
              Industries
            </button>
          </div>

          {/* Tab Content */}
          <div className="space-y-4">
            {activeTab === 'segments' && segments.map((segment, index) => (
              <CollapsibleCard
                key={index}
                title={segment.segmentName}
                description={segment.description}
                relevanceToBusiness={segment.relevanceToBusiness}
                actionableInsights={segment.actionableInsights}
                icon={<FaUsers />}
                isExpanded={expandedIndex === index}
                onToggle={() => setExpandedIndex(expandedIndex === index ? -1 : index)}
                showImage={true}
              />
            ))}

            {activeTab === 'industries' && industries.map((industry, index) => (
              <CollapsibleCard
                key={index}
                title={industry.name}
                description={industry.description}
                relevanceToBusiness={industry.relevanceToBusiness}
                actionableInsights={industry.actionableInsights}
                icon={<MdBusiness />}
                isExpanded={expandedIndex === index}
                onToggle={() => setExpandedIndex(expandedIndex === index ? -1 : index)}
                showImage={false}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Demographics;
